import React from 'react';
import { Link } from 'react-router-dom';
import ProductImageCarousel from '../ui/ProductImageCarousel';

interface PageHeaderButton {
  label: string;
  href?: string;
  to?: string;
  type: 'primary' | 'ghost';
  icon?: string;
}

interface PageHeaderProps {
  badge?: string;
  title: string;
  titleHighlight?: string;
  subtitle: string;
  showCTA?: boolean;
  buttons?: PageHeaderButton[];
  buttonsAlign?: 'left' | 'center' | 'right';
  bannerImages?: string[]; // Array of images to show in the banner
  imageAlt?: string; // Single alt text (fallback)
  bannerImagesAlt?: string[]; // Array of alt texts for banner images
  paddingY?: 'small' | 'medium' | 'large';
  backgroundImage?: string; // Background image for the header
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  badge,
  title,
  titleHighlight,
  subtitle,
  showCTA = false,
  buttons,
  buttonsAlign = 'center',
  bannerImages,
  imageAlt,
  bannerImagesAlt,
  paddingY = 'large',
  backgroundImage,
  children
}) => {
  const alignmentStyles: Record<'left' | 'center' | 'right', React.CSSProperties> = {
    left: { justifyContent: 'flex-start' },
    center: { justifyContent: 'center' },
    right: { justifyContent: 'flex-end' }
  };

  const paddingYStyles: Record<'small' | 'medium' | 'large', string> = {
    small: 'calc(var(--industrial-unit) * 6)',
    medium: 'calc(var(--industrial-unit) * 9)',
    large: 'calc(var(--industrial-unit) * 12)'
  };

  // Check if we have banner images to display
  const hasBannerImages = bannerImages && bannerImages.length > 0;

  // Build background style based on whether we have a background image
  const backgroundStyle = backgroundImage
    ? {
        backgroundImage: `linear-gradient(135deg, rgba(13, 49, 123, 0.85) 0%, rgba(48, 61, 82, 0.85) 50%, rgba(51, 60, 78, 0.85) 100%), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {
        background: 'linear-gradient(135deg, #0d317b 0%, #303d52 50%, #333c4e 100%)'
      };

  return (
    <div
      className="hero-industrial pattern-overlay"
      style={{
        padding: `${paddingYStyles[paddingY]} calc(var(--industrial-unit) * 12)`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        ...backgroundStyle
      }}
    >
      <div
        className={`hero-industrial-content ${hasBannerImages ? 'hero-industrial-two-column' : ''}`}
        style={{
          paddingTop: 0
        }}
      >
        <div>
          {badge && (
            <div className="hero-industrial-badge">
              <span>⬣</span>
              <span>{badge}</span>
            </div>
          )}

          <h1 className="hero-industrial-title">
            {title}
            {titleHighlight && (
              <>
                <br />
                <span style={{ color: 'var(--industrial-primary)' }}>{titleHighlight}</span>
              </>
            )}
          </h1>

          <p className="hero-industrial-subtitle">
            {subtitle}
          </p>

          {buttons && buttons.length > 0 && (
            <div
              className="hero-industrial-cta"
              style={alignmentStyles[buttonsAlign]}
            >
              {buttons.map((button, index) => {
                const className = `btn-industrial ${button.type === 'primary' ? 'btn-industrial-primary' : 'btn-industrial-ghost'}`;
                const content = (
                  <>
                    {button.icon && <span>{button.icon}</span>}
                    <span>{button.label}</span>
                  </>
                );

                // If 'to' prop is provided, use Link for React Router navigation
                if (button.to) {
                  return (
                    <Link key={index} to={button.to} className={className}>
                      {content}
                    </Link>
                  );
                }

                // Otherwise use regular anchor tag
                return (
                  <a key={index} href={button.href} className={className}>
                    {content}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {hasBannerImages && (
          <div className="hero-industrial-image">
            <ProductImageCarousel
              images={bannerImages}
              alt={imageAlt || title}
              alts={bannerImagesAlt}
            />
          </div>
        )}
      </div>

      {children && (
        <div className="hero-industrial-content" style={{ paddingTop: 0 }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
